import { useNavigate } from 'react-router-dom';
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Button, Divider, Menu, MenuItem, Select, Typography } from '@mui/material';
import * as Colors from '../theme/colors';
import AppLoader from '../components/AppLoader';
import SessionBar from '../components/SessionBar';
import RecordingNotice from '../components/RecordingNotice';
import ContractorAgreement from './ContractorAgreement';
import {
  ContractorOrganization,
  ContractorProfile,
  SessionSecurityControl,
  Target,
  TargetService,
  createSession,
  fetchSessionPolicy,
  fetchProfile,
  fetchTargets,
} from '../services/contractors';

const CONTENT_WIDTH = 1146;
const HAIRLINE = 'rgba(255,255,255,0.06)';

function describeType(services: TargetService[]): string {
  const desktop = services.find((service) => service.capability === 'desktop');
  if (desktop) {
    return desktop.port === 3389 ? 'Type: RDP Desktop' : 'Type: VNC Desktop';
  }
  return 'Type: Terminal';
}

const StatusLabel: React.FC<{ online: boolean }> = ({ online }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
    <Box
      sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: online ? Colors.Green : Colors.Red }}
    />
    <Typography sx={{ fontSize: 13, color: online ? Colors.Green : Colors.Red }}>
      {online ? 'Online' : 'Offline'}
    </Typography>
  </Box>
);

const AccessButton: React.FC<{
  label: string;
  disabled: boolean;
  busy: boolean;
  onClick: () => void;
}> = ({ label, disabled, busy, onClick }) => (
  <Button
    variant="outlined"
    disableElevation
    onClick={onClick}
    disabled={disabled || busy}
    sx={{ flex: 1, whiteSpace: 'nowrap', px: 1.5, minWidth: 0 }}
  >
    {busy ? 'Connecting' : label}
  </Button>
);

const ContractorPortal: React.FC = () => {
  const {
    isAuthenticated,
    isLoading,
    error: authError,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
  } = useAuth0();

  const navigate = useNavigate();
  const [profile, setProfile] = useState<ContractorProfile | null>(null);
  const [organization, setOrganization] = useState<ContractorOrganization | null>(null);
  const [targets, setTargets] = useState<Target[]>([]);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [pending, setPending] = useState<{
    target: Target;
    service: TargetService;
    policy: SessionSecurityControl;
  } | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (isLoading || isAuthenticated || authError) return;
    loginWithRedirect();
  }, [isLoading, isAuthenticated, authError, loginWithRedirect]);

  const loadProfile = useCallback(() => {
    getAccessTokenSilently()
      .then(fetchProfile)
      .then((loaded) => {
        setProfile(loaded);
        setOrganization(loaded.organizations[0] ?? null);
      })
      .catch((e: Error) => setError(e.message));
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (!isAuthenticated) return;
    loadProfile();
  }, [isAuthenticated, loadProfile]);

  const loadTargets = useCallback(
    async (org: ContractorOrganization) => {
      setLoadingTargets(true);
      setError(null);
      try {
        const token = await getAccessTokenSilently();
        setTargets(await fetchTargets(token, org.id));
      } catch (e) {
        setError((e as Error).message);
        setTargets([]);
      } finally {
        setLoadingTargets(false);
      }
    },
    [getAccessTokenSilently]
  );

  useEffect(() => {
    if (organization && profile?.agreement.acceptedAt) loadTargets(organization);
  }, [organization, profile, loadTargets]);

  const open = async (target: Target, service: TargetService, acknowledged: boolean) => {
    if (!organization) return;
    setConnecting(`${target.id}:${service.port}`);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const grant = await createSession(
        token,
        organization.id,
        target.id,
        service.port,
        acknowledged
      );
      const route = grant.capability === 'terminal' ? 'terminal' : 'desktop';
      navigate(`/${route}?organization=${organization.id}&resource=${target.id}&port=${service.port}`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setConnecting(null);
    }
  };

  const connect = async (target: Target, service: TargetService) => {
    if (!organization) return;
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const policy = await fetchSessionPolicy(token, organization.id);

      if (policy.enableSessionRecording) {
        setPending({ target, service, policy });
        return;
      }

      await open(target, service, false);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  if (authError) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: Colors.Black,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ textAlign: 'center', maxWidth: 420, px: 3 }}>
          <Typography sx={{ color: Colors.White, fontSize: 24, fontWeight: 700, mb: 1 }}>
            Sign in failed
          </Typography>
          <Typography sx={{ color: Colors.LightGray, mb: 4 }}>{authError.message}</Typography>
          <Button variant="contained" onClick={() => loginWithRedirect()}>
            Try again
          </Button>
        </Box>
      </Box>
    );
  }

  if (isLoading || !isAuthenticated) return <AppLoader />;

  if (profile && !profile.agreement.acceptedAt) {
    return <ContractorAgreement onSigned={loadProfile} />;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: Colors.Black }}>
      <SessionBar>
        {profile && profile.organizations.length > 1 && (
          <Select
            size="small"
            value={organization?.id ?? ''}
            onChange={(event) =>
              setOrganization(profile.organizations.find((o) => o.id === event.target.value) ?? null)
            }
            sx={{
              bgcolor: Colors.InputBackground,
              color: Colors.White,
              minWidth: 200,
              fontSize: 13,
              '& fieldset': { borderColor: HAIRLINE },
            }}
          >
            {profile.organizations.map((org) => (
              <MenuItem key={org.id} value={org.id}>
                {org.name}
              </MenuItem>
            ))}
          </Select>
        )}
      </SessionBar>

      <Box sx={{ pt: 9, px: 5, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography sx={{ color: Colors.White, fontSize: 28, fontWeight: 700, mb: 1.5 }}>
          Select Remote Desktop
        </Typography>
        <Typography sx={{ color: Colors.LightGray, fontSize: 14, mb: 5, textAlign: 'center' }}>
          {organization
            ? `These are the machines ${organization.name} has given you access to. Choose one to open a session.`
            : 'Choose a machine to open a session on.'}
        </Typography>

        {error && (
          <Typography sx={{ color: Colors.Red, fontSize: 14, mb: 4, textAlign: 'center' }}>
            {error}
          </Typography>
        )}

        {loadingTargets && <AppLoader />}

        {!loadingTargets && targets.length === 0 && !error && (
          <Typography sx={{ color: Colors.LightGray, fontSize: 14 }}>
            {profile && profile.organizations.length === 0
              ? 'You are not engaged by any organization yet.'
              : 'No machines have been shared with you yet.'}
          </Typography>
        )}

        <Box
          sx={{
            width: '100%',
            maxWidth: CONTENT_WIDTH,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(346px, 1fr))',
            gap: 3,
          }}
        >
          {targets.map((target) => {
            const terminal = target.services.find((s) => s.capability === 'terminal');
            const desktop = target.services.find((s) => s.capability === 'desktop');

            return (
              <Box
                key={target.id}
                sx={{
                  bgcolor: Colors.CardBackground,
                  borderRadius: '12px',
                  p: 3,
                }}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}
                >
                  <Box>
                    <Typography sx={{ color: Colors.White, fontSize: 16, fontWeight: 600 }}>
                      {target.name}
                    </Typography>
                    <Typography sx={{ color: Colors.HelperText, fontSize: 12, mt: 0.5 }}>
                      {describeType(target.services)}
                    </Typography>
                  </Box>
                  <StatusLabel online={target.online} />
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
                  <AccessButton
                    label="Access Terminal"
                    disabled={!terminal || !target.online}
                    busy={connecting === `${target.id}:${terminal?.port}`}
                    onClick={() => terminal && connect(target, terminal)}
                  />
                  <AccessButton
                    label="Access GUI"
                    disabled={!desktop || !target.online}
                    busy={connecting === `${target.id}:${desktop?.port}`}
                    onClick={() => desktop && connect(target, desktop)}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      <RecordingNotice
        open={pending !== null}
        policy={pending?.policy ?? null}
        targetName={pending?.target.name ?? ''}
        onAccept={() => {
          const accepted = pending;
          setPending(null);
          if (accepted) void open(accepted.target, accepted.service, true);
        }}
        onDecline={() => setPending(null)}
      />
    </Box>
  );
};

export default ContractorPortal;
